/**
 * TransferSelectionStep — transfer selection UI with week navigation and barcode scan.
 * Extracted from TransferVerification.tsx for readability.
 */
import React, { useState } from 'react';
import { useLanguage } from '../../../i18n/LanguageContext';
import { UnprocessedTransfer, TransferItem } from '../../../services/inventoryService';
import { toLocalISODate } from '../../../utils/dateUtils';
import { getWeekStart, getWeekStartForOffset, formatWeekRange, RangeDays } from './useTransferData';
import {
  CheckCircle,
  User,
  Package,
  Scan,
  RefreshCw,
  ArrowLeft,
  ChevronLeft,
  ChevronRight,
  Calendar,
} from 'lucide-react';
import DatePickerDropdown from '../../../components/DatePickerDropdown';

interface TransferSelectionStepProps {
  supervisorName: string;
  transfers: UnprocessedTransfer[];
  loadingTransfers: boolean;
  loadTransfers: () => void;
  weekOffset: number;
  setWeekOffset: React.Dispatch<React.SetStateAction<number>>;
  rangeDays: RangeDays;
  setRangeDays: React.Dispatch<React.SetStateAction<RangeDays>>;
  transferScanBuffer: string;
  setTransferScanBuffer: (v: string) => void;
  transferScanError: string;
  isLookingUpTransfer: boolean;
  handleTransferKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  handleSelectTransfer: (transfer: UnprocessedTransfer) => void;
  transferInputRef: React.RefObject<HTMLInputElement>;
  onResetBadge?: () => void;
}

const TransferSelectionStep: React.FC<TransferSelectionStepProps> = ({
  supervisorName,
  transfers,
  loadingTransfers,
  loadTransfers,
  weekOffset,
  setWeekOffset,
  rangeDays,
  setRangeDays,
  transferScanBuffer,
  setTransferScanBuffer,
  transferScanError,
  isLookingUpTransfer,
  handleTransferKeyDown,
  handleSelectTransfer,
  transferInputRef,
  onResetBadge,
}) => {
  const { t } = useLanguage();
  // WCO-3412: controlled input re-renders reset a direct inputMode DOM mutation — state keeps manual keyboard enabled after tap.
  const [allowKeyboard, setAllowKeyboard] = useState(false);

  return (
    <div className="p-4">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          {onResetBadge && (
            <button
              onClick={onResetBadge}
              className="hidden md:block p-1.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
              title={t('transferVerification.switchSupervisor') || 'Switch supervisor'}
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
          )}
          <div className="w-10 h-10 bg-green-100 dark:bg-green-800 rounded-full flex items-center justify-center">
            <User className="w-5 h-5 text-green-600 dark:text-green-400" />
          </div>
          <div>
            <p className="font-semibold text-gray-900 dark:text-white">{supervisorName}</p>
            <p className="text-xs text-green-600 dark:text-green-400 flex items-center gap-1">
              <CheckCircle className="w-3 h-3" />
              {t('operator.supervisorVerified')}
            </p>
          </div>
        </div>
        <button
          onClick={loadTransfers}
          disabled={loadingTransfers}
          className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
          title={t('common.refresh') || 'Refresh'}
        >
          <RefreshCw className={`w-5 h-5 ${loadingTransfers ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {/* Scan transfer barcode */}
      <div className="mb-4 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
        <div className="flex items-center gap-2 mb-2">
          <Scan className="w-5 h-5 text-blue-600 dark:text-blue-400" />
          <span className="text-sm font-medium text-blue-700 dark:text-blue-300">
            {t('transferVerification.scanTransferBarcode')}
          </span>
        </div>
        <input
          ref={transferInputRef}
          type="text"
          inputMode={allowKeyboard ? 'text' : 'none'}
          onTouchStart={() => setAllowKeyboard(true)}
          value={transferScanBuffer}
          onChange={(e) => setTransferScanBuffer(e.target.value)}
          onKeyDown={handleTransferKeyDown}
          placeholder={t('transferVerification.enterTransferNumber')}
          className="w-full px-3 py-2 text-sm border border-blue-300 dark:border-blue-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
          autoComplete="off"
          disabled={isLookingUpTransfer}
        />
        {transferScanError && (
          <p className="mt-2 text-sm text-red-600 dark:text-red-400">{transferScanError}</p>
        )}
        {isLookingUpTransfer && (
          <p className="mt-2 text-sm text-blue-600 dark:text-blue-400">{t('common.loading')}...</p>
        )}
      </div>

      {/* Date range selector — single row: date nav + 1d/7d/14d toggle */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-1">
          <button
            onClick={() => setWeekOffset(prev => prev + 1)}
            className="p-1.5 rounded-lg bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-600 dark:text-gray-300"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          <DatePickerDropdown
            selected={toLocalISODate(getWeekStartForOffset(weekOffset))}
            max={toLocalISODate(new Date())}
            onSelect={(date) => {
              const selectedDate = new Date(date + 'T00:00:00');
              const today = new Date();
              if (rangeDays === 1) {
                const diffDays = Math.round((today.getTime() - selectedDate.getTime()) / (24 * 60 * 60 * 1000));
                setWeekOffset(Math.max(0, diffDays));
              } else {
                const currentWeekStart = getWeekStart(today);
                const selectedWeekStart = getWeekStart(selectedDate);
                const divisor = rangeDays === 14 ? 14 : 7;
                const diff = Math.round((currentWeekStart.getTime() - selectedWeekStart.getTime()) / (divisor * 24 * 60 * 60 * 1000));
                setWeekOffset(Math.max(0, diff));
              }
            }}
          >
            {({ onClick }) => {
              const displayRange = (() => {
                const fmt = (d: Date) => `${d.getMonth() + 1}/${d.getDate()}`;
                if (rangeDays === 1) {
                  const d = new Date();
                  d.setDate(d.getDate() - weekOffset);
                  return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
                } else if (rangeDays === 14) {
                  const start = getWeekStartForOffset(0);
                  start.setDate(start.getDate() - (weekOffset * 14));
                  const end = new Date(start);
                  end.setDate(start.getDate() + 13);
                  return `${fmt(start)} - ${fmt(end)}`;
                }
                return formatWeekRange(weekOffset);
              })();
              return (
                <button
                  type="button"
                  onClick={onClick}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 cursor-pointer hover:bg-gray-200 dark:hover:bg-gray-600"
                >
                  <Calendar className="w-4 h-4 shrink-0" />
                  <span className="text-sm font-medium whitespace-nowrap">
                    {displayRange}
                  </span>
                </button>
              );
            }}
          </DatePickerDropdown>
          <button
            onClick={() => setWeekOffset(prev => Math.max(0, prev - 1))}
            disabled={weekOffset === 0}
            className="p-1.5 rounded-lg bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-600 dark:text-gray-300 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
        <div className="flex items-center bg-gray-100 dark:bg-gray-700 rounded-lg p-0.5 text-xs">
          {([1, 7, 14] as const).map((d) => (
            <button
              key={d}
              onClick={() => { setRangeDays(d); setWeekOffset(0); }}
              className={`px-2.5 py-1 rounded-md transition-colors font-medium ${rangeDays === d ? 'bg-white dark:bg-gray-500 text-gray-900 dark:text-white shadow-sm' : 'text-gray-500 dark:text-gray-400'}`}
            >
              {d}d
            </button>
          ))}
        </div>
      </div>

      {loadingTransfers ? (
        <div className="flex justify-center py-12">
          <RefreshCw className="w-8 h-8 animate-spin text-gray-400" />
        </div>
      ) : transfers.length === 0 ? (
        <div className="text-center py-12 text-gray-500 dark:text-gray-400">
          <Package className="w-12 h-12 mx-auto mb-3 opacity-50" />
          <p>{t('transferVerification.noTransfersPending')}</p>
        </div>
      ) : (
        <div className="space-y-2">
          {transfers.map((transfer) => {
            const pendingItems = transfer.items.filter((i: TransferItem) => !i.already_received);
            return (
              <button
                key={transfer.transfer_number}
                onClick={() => handleSelectTransfer(transfer)}
                className="w-full text-left px-3 py-2.5 border border-gray-200 dark:border-gray-700 border-l-4 border-l-amber-500 rounded-lg cursor-pointer active:bg-gray-100 dark:active:bg-gray-700 transition-all"
              >
                <div className="flex items-center justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <p className="flex items-baseline gap-2">
                      <span className="font-bold text-base text-gray-900 dark:text-white">
                        {transfer.transfer_number}
                      </span>
                      <span className="text-sm text-gray-400 dark:text-gray-500">
                        {transfer.transfer_date} {transfer.warehouse_code_from || transfer.location_code_from || '?'} → {transfer.warehouse_code_to || transfer.location_code_to || 'HTE'}
                      </span>
                    </p>
                    <p className="text-sm text-gray-500 dark:text-gray-400 truncate mt-0.5">
                      {pendingItems.slice(0, 3).map((item: TransferItem) => (
                        item.item_name || item.item_number
                      )).join(', ')}
                      {pendingItems.length > 3 && ` +${pendingItems.length - 3} more`}
                    </p>
                  </div>
                  <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300 whitespace-nowrap flex-shrink-0">
                    {pendingItems.length} {t('transferVerification.itemsPending')}
                  </span>
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default TransferSelectionStep;
